import { useEffect, useState } from 'react';
import { Film } from 'lucide-react';
import { Panel } from './Panel';
import { localVideo, fmtMB } from '@/show/localVideo';

const fmtS = (s) => (s > 90 ? `${Math.round(s / 60)} min` : `${Math.max(0, Math.round(s))} s`);

// Host setup: keep the training video on this laptop (chunked download or a picked file) so the show needs no network.
export const TrainingVideoPanel = () => {
  const [s, setS] = useState(localVideo.state());
  useEffect(() => {
    const off = localVideo.subscribe(() => setS(localVideo.state()));
    localVideo.init();
    return off;
  }, []);
  const d = s.dl;
  const busy = d.status === 'downloading';
  const pct = d.total ? Math.round((d.bytes / d.total) * 100) : 0;
  return (
    <Panel testId="training-video-panel" code="10 / MEDIA" title="Training video" right={<Film size={18} className="text-[var(--teal)]" />}>
      <div className="space-y-3 text-sm">
        <div data-testid="training-video-saved-status" className={s.local ? 'text-[var(--spore)]' : 'text-[var(--amber)]'}>
          {s.local ? (s.local.origin === 'file' ? `VIDEO: FROM THIS COMPUTER ✓ (${s.local.name}, ${fmtMB(s.local.size)})` : `SAVED ON THIS LAPTOP ✓ (${fmtMB(s.local.size)})`) : busy ? 'SAVING TO THIS LAPTOP…' : 'NOT SAVED · PLAYS BY STREAMING'}
        </div>
        {s.fileError && <div data-testid="training-video-file-error" className="text-xs text-[var(--amber)]">{s.fileError}</div>}
        {s.local && (
          <div data-testid="training-video-saved-detail" className="text-xs text-[var(--muted)]">
            {s.local.origin === 'file' ? `FILE FROM THIS COMPUTER · ${s.local.name}` : `DOWNLOADED · ${s.local.name}`} · {fmtMB(s.local.size)} · {s.persisted ? 'PERSISTENT STORAGE ✓' : 'STORAGE MAY BE EVICTED'}
          </div>
        )}
        {(busy || (!s.local && d.total > 0)) && (
          <div data-testid="training-video-download-progress" className="text-xs text-[var(--muted)]">
            <div>
              {d.variant ? d.variant.toUpperCase() : ''} {fmtMB(d.bytes)} / {fmtMB(d.total)} · {pct}%{busy && d.speed ? ` · ${fmtMB(d.speed)}/s · ${fmtS(d.eta)} left` : ''}
            </div>
            <div className="mt-1 h-1 bg-white/10"><div className="h-1 bg-[var(--spore)] transition-[width] duration-300" style={{ width: `${pct}%` }} /></div>
          </div>
        )}
        {d.error && !s.local && <div data-testid="training-video-download-error" className="text-xs text-[var(--amber)]">{d.error}{/info HTTP/.test(d.error) ? ' · the server has no video: pick the file below' : ''}</div>}
        <div className="flex flex-wrap gap-3">
          {!s.local && <button type="button" data-testid="training-video-save-button" className="btn-lab" disabled={busy} onClick={() => localVideo.download()}>{busy ? 'SAVING…' : 'SAVE VIDEO TO THIS LAPTOP'}</button>}
          {s.local && <button type="button" data-testid="training-video-redownload-button" className="btn-lab" disabled={busy} onClick={() => localVideo.download({ force: true })}>Re-download</button>}
          {s.local && <button type="button" data-testid="training-video-remove-button" className="btn-lab" disabled={busy} onClick={() => localVideo.remove()}>Remove</button>}
          <label data-testid="training-video-file-label" className="btn-lab cursor-pointer">
            USE A VIDEO FILE FROM THIS COMPUTER
            <input
              type="file"
              accept="video/*"
              data-testid="training-video-file-input"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (f) localVideo.useFile(f);
                e.target.value = '';
              }}
            />
          </label>
        </div>
      </div>
    </Panel>
  );
};
